import { generatePath, useNavigate } from 'react-router-dom';

import { createForm, useCreateFormStore } from '@components/form';
import { IconEdit } from '@components/icons/edit';
import { IconIntegration } from '@components/icons/integration';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { useScopedTranslate } from '@locale/translate';

import { useCreateIntegration, useEditIntegration } from 'api/integrations/hooks';
import { Integration, IntegrationBase } from 'api/integrations/requests';
import { routePaths } from 'ui/authenticated/navigation';

const Form = createForm<IntegrationBase>();

export const useIntegrationAddEditSidebar = () =>
  useSidebar(IntegrationAddEditSidebar, { variant: 'REGULAR' });

const IntegrationAddEditSidebar = ({ integration }: { integration?: Integration }) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateIntegration();
  const editMutation = useEditIntegration();
  const navigate = useNavigate();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();

  const isEdit = Boolean(integration);

  const handleSubmit = async (data: IntegrationBase) => {
    if (isEdit) {
      await editMutation.mutateAsync({ integrationId: integration!.shortId, data });
      hideSidebar();
    } else {
      const response = await createMutation.mutateAsync(data);
      navigate(
        generatePath(routePaths.integrationDetail, { integrationId: response.shortId, '*': null })
      );
    }
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={isEdit ? IconEdit : IconIntegration}
        iconColor="elementBlue100"
        title={isEdit ? t('editIntegration') : t('addIntegration')}
        color="textPrimary"
      />
      <Sidebar.Content>
        <Form onSubmit={handleSubmit} store={formStore}>
          <Form.LogoField
            // @ts-ignore: This fails because logo is optional in the type
            id="logo"
            label={t('integrationLogo')}
            defaultImage={integration?.logo}
            dataTestid="upload-image"
          />
          <Form.TextField
            id="name"
            label={t('name')}
            placeholder={t('namePlaceholder')}
            validators={['required']}
            defaultValue={integration?.name}
          />
          <Form.Textarea
            id="description"
            label={t('description')}
            placeholder={t('descriptionPlaceholder')}
            defaultValue={integration?.description}
          />
          <Sidebar.Footer>
            <Form.Actions>
              <Form.Reset
                text={t('core.cancel')}
                variant="secondary"
                onClick={hideSidebar}
                isDisabled={createMutation.isLoading}
              />
              <Form.Submit
                text={isEdit ? t('updateIntegration') : t('addIntegration')}
                variant="primary-blue"
                isDisabled={!isDirty || createMutation.isLoading}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    addIntegration: 'Add custom integration',
    editIntegration: 'Edit integration',
    updateIntegration: 'Update integration',
    name: 'Name',
    namePlaceholder: 'Integration name',
    description: 'Description',
    descriptionPlaceholder: 'Short description',
    url: 'URL',
    submit: 'Submit',
    exampleRequest: 'Example request',
    integrationLogo: 'Integration logo',
  },
};
